import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Shield, 
  Users,
  Menu,
  X,
  LogOut,
  User,
  Building2,
  ChevronDown,
  CreditCard,
  GraduationCap,
  Image,
  BookOpen,
  UserCheck,
  ClipboardList,
  Bus,
  Activity,
  AlertTriangle,
  UserCog,
  BarChart2,
  Bell,
  ShoppingBag,
  Search,
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getCoachingAdminPermissions } from '../../firebase/collections';
import LanguageSelector from '../LanguageSelector';
import TranslationDisclaimer from '../TranslationDisclaimer';
import toast from 'react-hot-toast';

const CoachingAdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [allowedSections, setAllowedSections] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();

  // Load coaching admin permissions
  useEffect(() => {
    const loadPermissions = async () => {
      if (!currentUser?.email) {
        setLoadingPermissions(false);
        return;
      }

      // Check if user is founder, superadmin, or regular admin - they have full access
      const isFounder = userProfile?.role === 'founder' || currentUser.email.includes('founder');
      const isSuperAdmin = userProfile?.role === 'superadmin';
      const isRegularAdmin = userProfile?.role === 'admin';
      
      if (isFounder || isSuperAdmin || isRegularAdmin) {
        setAllowedSections(null); // null means all sections allowed
        setLoadingPermissions(false);
        return;
      }

      // For coaching_admin role, check their email-based permissions
      if (userProfile?.role === 'coaching_admin') {
        try {
          const result = await getCoachingAdminPermissions(currentUser.email);
          
          if (result.success && result.data && result.data.permissions && result.data.permissions.length > 0) {
            // Has specific permissions set
            setAllowedSections(result.data.permissions);
          } else {
            // No permissions document OR empty permissions array = full access
            setAllowedSections(null); // null means all sections
          }
        } catch (error) {
          console.error('Error loading permissions:', error);
          setAllowedSections(null); // Default to full access on error
        } finally {
          setLoadingPermissions(false);
        }
        return;
      }

      // For coaching_staff, use their accessibleSections from user profile
      if (userProfile?.role === 'coaching_staff') {
        setAllowedSections(userProfile?.accessibleSections || ['dashboard']);
        setLoadingPermissions(false);
        return;
      }

      // Default: dashboard only
      setAllowedSections(['dashboard']);
      setLoadingPermissions(false);
    };

    loadPermissions();
  }, [currentUser?.email, userProfile?.role]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Auto-expand admissions section if on admissions page
  useEffect(() => {
    if (location.pathname.startsWith('/coaching-admin/admissions')) {
      setExpandedSections(prev => ({ ...prev, admissions: true }));
    }
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  // Limited navigation for coaching admin
  const navigation = [
    {
      name: 'Dashboard',
      href: '/coaching-admin',
      icon: Home,
    },
    {
      name: 'Admissions',
      href: '/coaching-admin/admissions',
      icon: GraduationCap,
      key: 'admissions',
      children: [
        { name: 'Profile & Info', href: '/coaching-admin/admissions/profile' },
        { name: 'Application Form', href: '/coaching-admin/admissions/form' },
        { name: 'Curriculum', href: '/coaching-admin/admissions/curriculum' },
        { name: 'Brochures & Posters', href: '/coaching-admin/admissions/materials' },
        { name: 'Faculty', href: '/coaching-admin/admissions/faculty' },
        { name: 'Applications', href: '/coaching-admin/admissions/applications' },
        { name: 'Admission Tests', href: '/coaching-admin/admissions/tests' },
        { name: 'Fee Structure', href: '/coaching-admin/admissions/fee-structure' },
        { name: 'Our Results', href: '/coaching-admin/admissions/results' },
      ]
    },
    {
      name: 'Test Management',
      href: '/coaching-admin/tests',
      icon: FileText,
    },
    {
      name: 'Exam Management',
      href: '/coaching-admin/exams',
      icon: Shield,
    },
    {
      name: 'Resource Management',
      href: '/coaching-admin/resources',
      icon: FileText,
    },
    {
      name: 'Class Management',
      href: '/coaching-admin/batches',
      icon: Users,
    },
    {
      name: 'Student Management',
      href: '/coaching-admin/students',
      icon: UserCog,
    },
    {
      name: 'Student Info',
      href: '/coaching-admin/student-info',
      icon: Search,
    },
    {
      name: 'Teacher Management',
      href: '/coaching-admin/teachers',
      icon: GraduationCap,
    },
    {
      name: 'Teacher Settlements',
      href: '/coaching-admin/settlements',
      icon: CreditCard,
    },
    {
      name: 'Attendance',
      href: '/coaching-admin/attendance',
      icon: UserCheck,
    },
    {
      name: 'Transport Management',
      href: '/coaching-admin/transport',
      icon: Bus,
    },
    {
      name: 'Activities Management',
      href: '/coaching-admin/activities',
      icon: Activity,
    },
    {
      name: 'Grievance Management',
      href: '/coaching-admin/grievance',
      icon: AlertTriangle,
    },
    {
      name: 'Leave Management',
      href: '/coaching-admin/leave',
      icon: ClipboardList,
    },
    {
      name: 'Fee Management',
      href: '/coaching-admin/fees',
      icon: CreditCard,
    },
    {
      name: 'Report Cards',
      href: '/coaching-admin/report-cards',
      icon: FileText,
    },
    {
      name: 'Data Analytics',
      href: '/coaching-admin/analytics',
      icon: BarChart2,
    },
    {
      name: 'ID Cards',
      href: '/coaching-admin/id-cards',
      icon: CreditCard,
    },
    {
      name: 'Store Management',
      href: '/coaching-admin/store',
      icon: ShoppingBag,
    },
    {
      name: 'Hostel & Mess',
      href: '/coaching-admin/hostel',
      icon: Building2,
    },
    {
      name: 'Subscription',
      href: '/coaching-admin/subscription',
      icon: CreditCard,
    },
    {
      name: 'Staff Management',
      href: '/coaching-admin/staff',
      icon: UserCheck,
    },
  ];

  // Helper function to check if section is allowed
  const isSectionAllowed = (itemName) => {
    // Map navigation items to section IDs
    const sectionMapping = {
      'Dashboard': 'dashboard',
      'Admissions': 'admissions',
      'Test Management': 'tests',
      'Exam Management': 'exams',
      'Resource Management': 'resources',
      'Class Management': 'batches',
      'Student Management': 'students',
      'Student Info': 'students',
      'Teacher Management': 'teachers',
      'Teacher Settlements': 'settlements',
      'Attendance': 'attendance',
      'Transport Management': 'transport',
      'Activities Management': 'activities',
      'Grievance Management': 'grievance',
      'Leave Management': 'leave',
      'Fee Management': 'fees',
      'Report Cards': 'report-cards',
      'Data Analytics': 'analytics',
      'ID Cards': 'id-cards',
      'Store Management': 'store',
      'Hostel & Mess': 'hostel',
      'Subscription': 'subscription',
      'Staff Management': 'staff'
    };

    const sectionId = sectionMapping[itemName];

    // If no permissions set (null), allow all
    if (allowedSections === null) return true;

    // Check if section is in allowed list
    return allowedSections.includes(sectionId);
  };

  // Filter navigation based on user permissions
  const filteredNavigation = loadingPermissions ? [] : navigation.filter(item => {
    // If user is founder, superadmin, or regular admin - show everything
    if (userProfile?.role === 'founder' || 
        userProfile?.role === 'superadmin' || 
        userProfile?.role === 'admin') {
      return true;
    }
    
    // If user is coaching admin, check their email-based permissions
    if (userProfile?.role === 'coaching_admin') {
      return isSectionAllowed(item.name);
    }
    
    // If user is coaching staff, check accessibleSections
    if (userProfile?.role === 'coaching_staff') {
      const accessibleSections = userProfile?.accessibleSections || [];
      const sectionMapping = {
        'Dashboard': 'dashboard',
        'Admissions': 'admissions',
        'Test Management': 'tests',
        'Exam Management': 'exams',
        'Resource Management': 'resources',
        'Class Management': 'batches',
        'Student Management': 'students',
        'Student Info': 'students',
        'Teacher Management': 'teachers',
        'Teacher Settlements': 'settlements',
        'Attendance': 'attendance',
        'Transport Management': 'transport',
        'Activities Management': 'activities',
        'Grievance Management': 'grievance',
        'Leave Management': 'leave',
        'Fee Management': 'fees',
        'Report Cards': 'report-cards',
        'Data Analytics': 'analytics',
        'ID Cards': 'id-cards',
        'Store Management': 'store',
        'Hostel & Mess': 'hostel',
        'Subscription': 'subscription',
        'Staff Management': 'staff',
      };

      const sectionId = sectionMapping[item.name];
      return accessibleSections.includes(sectionId);
    }
    
    return false;
  });

  const isActive = (href) => {
    if (href === '/coaching-admin') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Translation Disclaimer */}
      <TranslationDisclaimer />
      
      {/* Mobile Header */}
      <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <Building2 className="text-primary-600" size={24} />
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {userProfile?.coachingName || 'Coaching Admin'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {/* Language Selector */}
            <LanguageSelector />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out`}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <Building2 className="text-primary-600" size={28} />
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                    Institute Admin
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {userProfile?.coachingName || 'Admin Panel'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4">
              {loadingPermissions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : filteredNavigation.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No sections available</p>
                </div>
              ) : (
              <div className="space-y-1">
                {filteredNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const hasChildren = item.children && item.children.length > 0;
                  const isExpanded = expandedSections[item.key] || false;
                  
                  return (
                    <div key={item.href}>
                      {hasChildren ? (
                        <button
                          onClick={() => toggleSection(item.key)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            active
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon size={20} />
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <ChevronDown 
                            size={16} 
                            className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          onClick={() => {
                            if (window.innerWidth < 1024) {
                              setSidebarOpen(false);
                            }
                          }}
                          className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                            active
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Icon size={20} />
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      )}
                      {hasChildren && isExpanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => {
                            const childActive = location.pathname === child.href;
                            return (
                              <Link
                                key={child.href}
                                to={child.href}
                                onClick={() => {
                                  if (window.innerWidth < 1024) {
                                    setSidebarOpen(false);
                                  }
                                }}
                                className={`block px-4 py-2 rounded-lg text-sm transition-colors ${
                                  childActive
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              >
                                {child.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </nav>

            {/* MahStry Research Hub Button */}
            <div className="px-4 pb-4">
              <a
                href="https://mahstry-resarch.web.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-pink-500/20 hover:bg-pink-500/30 backdrop-blur-md border border-pink-300/30 text-pink-600 dark:text-pink-400 rounded-lg transition-all duration-300 font-medium shadow-lg hover:shadow-pink-500/20"
              >
                <span>🔬</span>
                <span>MahStry Research Hub</span>
              </a>
            </div>

            {/* User Menu */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="relative user-menu">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white">
                    <User size={18} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {userProfile?.contactPerson || currentUser?.displayName || 'Admin'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {currentUser?.email}
                    </p>
                  </div>
                  <ChevronDown size={16} className="text-gray-500" />
                </button>

                {showUserMenu && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-4 py-3 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <LogOut size={18} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-0 flex flex-col min-h-screen">
          {/* Desktop Header */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
            <div className="flex items-center justify-between px-6 py-3">
              <div className="flex items-center space-x-3">
                <Building2 className="text-primary-600" size={24} />
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    {userProfile?.coachingName || 'Coaching Admin'}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Admin Dashboard
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Language Selector */}
                <LanguageSelector />
              </div>
            </div>
          </div>
          
          <div className="flex-1 p-4 md:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CoachingAdminLayout;
